import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "admin" | "sub_admin" | null;

interface AdminAuthContextType {
  isAdmin: boolean;
  userRole: UserRole;
  loading: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

const getTodayRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { today, tomorrow };
};

export const AdminAuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRole = async (userId: string): Promise<UserRole> => {
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();
      return (data?.role as UserRole) || null;
    } catch (e) {
      console.error("fetchRole error:", e);
      return null;
    }
  };

  const handleDailyLog = async (userId: string, email: string) => {
    const { today, tomorrow } = getTodayRange();

    try {
      const { data: existingLog } = await supabase
        .from("employee_logs")
        .select("id")
        .eq("user_id", userId)
        .gte("created_at", today.toISOString())
        .lt("created_at", tomorrow.toISOString())
        .limit(1)
        .maybeSingle();

      if (!existingLog) {
        await supabase.from("employee_logs").insert({
          user_id: userId,
          email,
          event: "login",
          duration_minutes: 0,
          last_active_at: new Date().toISOString(),
        } as any);
      }
    } catch (e) {
      console.error("Daily log error:", e);
    }
  };

  const updateHeartbeat = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { today, tomorrow } = getTodayRange();

      await supabase
        .from("employee_logs")
        .update({ last_active_at: new Date().toISOString() } as any)
        .eq("user_id", session.user.id)
        .gte("created_at", today.toISOString())
        .lt("created_at", tomorrow.toISOString())
        .is("logged_out_at", null);
    } catch (e) {
      // Silent fail for heartbeat
    }
  };

  const flushSessionOnUnload = () => {
    const sessionStartStr = sessionStorage.getItem("session_start");
    if (!sessionStartStr) return;

    const sessionStart = parseInt(sessionStartStr, 10);
    const sessionDurationMinutes = Math.round((Date.now() - sessionStart) / 60000);

    // Use sendBeacon for best-effort logout tracking
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/update_employee_logout`;
    // sendBeacon doesn't support custom headers well for RPC, so we'll use a simpler approach
    // Instead, update via fetch with keepalive
    const { today, tomorrow } = getTodayRange();

    const body = JSON.stringify({
      logged_out_at: new Date().toISOString(),
      last_active_at: new Date().toISOString(),
    });

    // Best-effort: use the supabase REST API directly with keepalive
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = sessionStorage.getItem("sb-access-token");

    // We can't reliably do complex queries in beforeunload, so just update last_active_at
    // The EmployeeLogsView will use the 5-min staleness check
    if (supabaseUrl && supabaseKey) {
      navigator.sendBeacon(
        `${supabaseUrl}/rest/v1/employee_logs?user_id=eq.${sessionStorage.getItem("sb-user-id")}&created_at=gte.${today.toISOString()}&created_at=lt.${tomorrow.toISOString()}&logged_out_at=is.null`,
        new Blob([JSON.stringify({
          logged_out_at: new Date().toISOString(),
          duration_minutes: sessionDurationMinutes,
          last_active_at: new Date().toISOString(),
        })], { type: "application/json" })
      );
    }

    sessionStorage.removeItem("session_start");
  };

  const startHeartbeat = (userId: string) => {
    // Store user id for beforeunload
    sessionStorage.setItem("sb-user-id", userId);

    // Heartbeat every 2 minutes
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(updateHeartbeat, 2 * 60 * 1000);

    window.addEventListener("beforeunload", flushSessionOnUnload);
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    window.removeEventListener("beforeunload", flushSessionOnUnload);
  };

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const role = await fetchRole(session.user.id);

          if (role) {
            setUserRole(role);
            setIsAdmin(true);

            if (!sessionStorage.getItem("session_start")) {
              sessionStorage.setItem("session_start", Date.now().toString());
              await handleDailyLog(session.user.id, session.user.email || "");
            }

            startHeartbeat(session.user.id);
          } else {
            await supabase.auth.signOut();
            setIsAdmin(false);
            setUserRole(null);
          }
        }
      } catch (e) {
        console.error("Auth init error:", e);
      } finally {
        setLoading(false);
      }
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === "SIGNED_OUT" || !session) {
        setIsAdmin(false);
        setUserRole(null);
        sessionStorage.removeItem("session_start");
        stopHeartbeat();
      }
    });

    return () => {
      subscription.unsubscribe();
      stopHeartbeat();
    };
  }, []);

  const login = async (email: string, password: string): Promise<string | null> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return error.message;

    const role = await fetchRole(data.user.id);

    if (!role) {
      await supabase.auth.signOut();
      return "Access denied: Account does not have admin privileges.";
    }

    setUserRole(role);
    setIsAdmin(true);

    sessionStorage.setItem("session_start", Date.now().toString());
    await handleDailyLog(data.user.id, data.user.email || "");
    startHeartbeat(data.user.id);

    return null;
  };

  const logout = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const userId = session.user.id;
        const sessionStartStr = sessionStorage.getItem("session_start");

        if (sessionStartStr) {
          const sessionStart = parseInt(sessionStartStr, 10);
          const sessionDurationMinutes = Math.round((Date.now() - sessionStart) / 60000);

          const { today, tomorrow } = getTodayRange();

          const { data: existingLog } = await supabase
            .from("employee_logs")
            .select("id, duration_minutes")
            .eq("user_id", userId)
            .gte("created_at", today.toISOString())
            .lt("created_at", tomorrow.toISOString())
            .limit(1)
            .maybeSingle();

          if (existingLog) {
            await supabase
              .from("employee_logs")
              .update({
                logged_out_at: new Date().toISOString(),
                last_active_at: new Date().toISOString(),
                duration_minutes: (existingLog.duration_minutes || 0) + sessionDurationMinutes,
              } as any)
              .eq("id", existingLog.id);
          }
        }
      }
    } catch (e) {
      console.error("logout error:", e);
    }

    stopHeartbeat();
    sessionStorage.removeItem("session_start");
    sessionStorage.removeItem("sb-user-id");
    await supabase.auth.signOut();
    setIsAdmin(false);
    setUserRole(null);
  };

  return (
    <AdminAuthContext.Provider value={{ isAdmin, userRole, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextType => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    return {
      isAdmin: false,
      userRole: null,
      loading: true,
      login: async () => "Not initialized",
      logout: async () => {},
    };
  }
  return ctx;
};
